/**
 * KB-META
 * Purpose: Auth guard
 * Role: ui.service
 * EntryPoints: constructor(), if()
 * Inputs: private authService:AuthService; private router:Router; private awsService:AwsService; private sessionService:SessionService; !version || version !== serverVersion:unknown
 * Outputs:
 * Validation:
 * Dependencies: AuthService, Router, AwsService, SessionService
 * Contracts:
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability:
 * Router: shard=partner-portal-cura; H2=guard/auth.guard; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Injectable } from "@angular/core";
import {
	CanActivate,
	ActivatedRouteSnapshot,
	RouterStateSnapshot,
	Router,
} from "@angular/router";
import { Observable, from, of } from "rxjs";
import { map, catchError, switchMap, tap } from "rxjs/operators";
import { AuthService } from "../services/auth.service";
import { AwsService } from "../services/aws.service";
import { SessionService } from "../services/session.service";
import { JwtHelperService } from "@auth0/angular-jwt";

interface RouteData {
	expectedRole: string | string[];
}

@Injectable({
	providedIn: "root",
})
export class AuthGuard implements CanActivate {
	constructor(
		private authService: AuthService,
		private router: Router,
		private awsService: AwsService,
		private sessionService: SessionService
	) {}

	private jwtHelper: JwtHelperService = new JwtHelperService();

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<boolean> {
		// Use retry logic for session check
		return from(this.checkAuthWithRetry()).pipe(
			switchMap((isAuthenticated) => {
				if (!isAuthenticated) {
					console.log(
						"AuthGuard: Authentication check failed after retries"
					);
					this.router.navigate(["login"], {
						queryParams: { returnUrl: state.url },
					});
					return of(false);
				}

				// Only check version if user is authenticated
				return this.checkVersion().pipe(
					switchMap(() => {
						// Get user details again after authentication is confirmed
						const userDetails = this.sessionService.getItem("user");
						const expectedRoles = (route.data as RouteData)
							.expectedRole;
						let userRole =
							userDetails && typeof userDetails === "object"
								? userDetails["profile"]
								: undefined;

						// Fallback: derive role from token if session 'user' is not hydrated yet
						if (!userRole) {
							const token = this.sessionService.getItem("token");
							if (token) {
								try {
									const decoded: any =
										this.jwtHelper.decodeToken(token);
									userRole = decoded?.profile;
								} catch {
									/* no-op */
								}
							}
						}

						// If no role is required, allow access
						if (!expectedRoles) {
							return of(true);
						}

						// Convert single role to array for consistent handling
						const roles = Array.isArray(expectedRoles)
							? expectedRoles
							: [expectedRoles];

						// Check if user's role is in the allowed roles (route guard always uses real roles)
						if (!roles.includes(userRole)) {
							console.log("AuthGuard: User role not authorized");
							this.router.navigate(["login"], {
								queryParams: { returnUrl: state.url },
							});
							return of(false);
						}

						return of(true);
					}),
					catchError((error) => {
						console.error(
							"AuthGuard: Error in route check:",
							error
						);
						this.router.navigate(["login"]);
						return of(false);
					})
				);
			})
		);
	}

    private async checkAuthWithRetry(maxRetries: number = 5): Promise<boolean> {
		for (let attempt = 0; attempt < maxRetries; attempt++) {
			try {
				const userDetails = this.sessionService.getItem("user");
				const token = this.sessionService.getItem("token");

				if (
					userDetails &&
					token &&
					this.authService.isLoggedIn &&
					!this.authService.isTokenExpired()
				) {
					return true;
				}

                if (attempt < maxRetries - 1) {
					console.log(
						`AuthGuard: Auth check attempt ${attempt + 1} failed, retrying...`
					);
					// Small delay to allow session to settle
                    await new Promise((resolve) => setTimeout(resolve, 250));
				}
			} catch (error) {
				console.error(
					`AuthGuard: Error during auth check attempt ${attempt + 1}:`,
					error
				);
                if (attempt < maxRetries - 1) {
                    await new Promise((resolve) => setTimeout(resolve, 250));
				}
			}
		}

		console.log("AuthGuard: All auth check attempts failed");
		return false;
	}

	private checkVersion(): Observable<void> {
		return from(this.awsService.get("/assets/json/version.json")).pipe(
			switchMap((data: any) => {
				if (!data || !data.version) {
					return of(undefined);
				}

				const version = this.sessionService.getItem("appVersion");
				const serverVersion = data.version;

				// First-run: set version without reloading to avoid disrupting fresh logins
				if (!version) {
					this.sessionService.setItem("appVersion", serverVersion);
					return of(undefined);
				}

				// Subsequent runs: only reload when version actually changes
				if (version !== serverVersion) {
					this.sessionService.setItem("appVersion", serverVersion);
					return from(this.clearCaches()).pipe(
						tap(() => {
							setTimeout(() => window.location.reload(), 500);
						})
					);
				}
				return of(undefined);
			}),
			catchError((error) => {
				console.error("Error checking version:", error);
				// Don't trigger reload on version check error
				return of(undefined);
			})
		);
	}

	private clearCaches(): Promise<void> {
		return caches
			.keys()
			.then((cacheNames) =>
				Promise.all(
					cacheNames.map((cacheName) => caches.delete(cacheName))
				)
			)
			.then(() => undefined);
	}
}
